import { Button, Paper, Radio, SegmentedControl, Stack, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { usersApi } from "../lib/api";
import { ApiError } from "../lib/apiClient";
import type { NotificationPreferences } from "../types/api";

export function NotificationPreferencesCard() {
  const queryClient = useQueryClient();
  const prefsQuery = useQuery({
    queryKey: ["notification-preferences"],
    queryFn: usersApi.getNotificationPreferences,
  });

  const [draft, setDraft] = useState<Pick<NotificationPreferences, "mode" | "summary_cadence"> | null>(
    null,
  );
  const mode = draft?.mode ?? prefsQuery.data?.mode ?? "all";
  const cadence = draft?.summary_cadence ?? prefsQuery.data?.summary_cadence ?? "daily";

  const saveMutation = useMutation({
    mutationFn: (payload: Pick<NotificationPreferences, "mode" | "summary_cadence">) =>
      usersApi.patchNotificationPreferences({
        mode: payload.mode,
        summary_cadence: payload.mode === "summary" ? payload.summary_cadence ?? "daily" : null,
      }),
    onSuccess: (preferences) => {
      queryClient.setQueryData(["notification-preferences"], preferences);
      setDraft(null);
      notifications.show({ color: "green", title: "Saved", message: "Notification preferences updated." });
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : "Something went wrong";
      notifications.show({ color: "red", title: "Could not save", message });
    },
  });

  function handleSave() {
    saveMutation.mutate({
      mode,
      summary_cadence: mode === "summary" ? cadence : null,
    });
  }

  const dirty =
    prefsQuery.data &&
    (prefsQuery.data.mode !== mode ||
      (mode === "summary" && (prefsQuery.data.summary_cadence ?? "daily") !== cadence));

  return (
    <Paper withBorder p="lg">
      <Title order={4} mb="xs">
        Notification preferences
      </Title>
      <Text size="sm" c="dimmed" mb="md">
        In-app notifications always appear immediately. Email and WhatsApp follow your frequency.
        Critical updates (payouts, KYC, disputes) are always sent right away.
      </Text>

      <Stack gap="md">
        <Radio.Group
          value={mode}
          onChange={(value) =>
            setDraft({
              mode: value as "all" | "summary",
              summary_cadence: cadence,
            })
          }
          label="Email & WhatsApp frequency"
        >
          <Stack gap="xs" mt="xs">
            <Radio value="all" label="All updates" description="Get notified as things happen" />
            <Radio value="summary" label="Summary" description="Batch non-critical updates into one message" />
          </Stack>
        </Radio.Group>

        {mode === "summary" && (
          <div>
            <Text size="sm" fw="var(--axio-font-weight-medium)" mb="var(--axio-unit-6)">
              Summary cadence
            </Text>
            <SegmentedControl
              value={cadence}
              onChange={(value) =>
                setDraft({
                  mode,
                  summary_cadence: value as "daily" | "weekly",
                })
              }
              data={[
                { label: "Daily", value: "daily" },
                { label: "Weekly", value: "weekly" },
              ]}
            />
          </div>
        )}

        <Button
          color="violet"
          onClick={handleSave}
          loading={saveMutation.isPending}
          disabled={!dirty}
          w="fit-content"
        >
          Save preferences
        </Button>
      </Stack>
    </Paper>
  );
}
